import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { CheckCircle2, Phone, ArrowRight, Lock } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const SERVICES = [
  "House Removals",
  "Office Moves",
  "Furniture Delivery",
  "Packing & Unpacking",
  "Storage Solutions",
  "Same Day Moving",
];

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const lenis = useLenis();

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [lenis]);

  useEffect(() => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "quote_thank_you", page: "thank-you" });
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex";
      document.head.appendChild(meta);
      return () => document.head.removeChild(meta);
    } catch {
      /* tracking must never break the page */
    }
  }, []);

  const params = new URLSearchParams();
  ["pickup_suburb", "drop_suburb", "moving_date", "email", "mobile", "bedrooms"].forEach((k) => {
    const v = searchParams.get(k);
    if (v) params.set(k, v);
  });
  const onlineLink = `/book-online${params.toString() ? `?${params}` : ""}`;

  return (
    <main className="min-h-screen">
      <Header />
      <section className="relative" data-testid="thanks-page">
        <div className="mx-auto max-w-3xl px-5 pb-24 pt-40 text-center lg:pt-48">
          <Reveal>
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-11 w-11 text-green-600" strokeWidth={1.6} />
            </span>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Quote Request Received</p>
            <h1 className="mt-3 font-display text-5xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-6xl" data-testid="thanks-heading">
              Thank <span className="text-blaze">You!</span>
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg" data-testid="thanks-message">
              Thank you for requesting a quote. We&rsquo;ve received your details.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10">
              <a
                href={onlineLink}
                data-testid="thanks-secure-booking-btn"
                className="group inline-flex items-center gap-3 rounded-xl bg-blaze px-10 py-5 text-sm font-bold uppercase tracking-wider text-white shadow-lg transition-all duration-300 hover:scale-[1.04] hover:bg-blue-700 hover:shadow-[0_12px_40px_rgba(37,99,235,0.4)]"
              >
                <Lock className="h-4 w-4" />
                Secure Booking Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-slate-500" data-testid="thanks-not-ready">
                Someone from our team will call you within{" "}
                <span className="font-semibold text-slate-900">10–15 minutes</span> with your quote.
              </p>
              <p className="mt-3 text-sm text-slate-500">
                Prefer to talk? Call{" "}
                <a href="tel:0391136005" className="font-semibold text-blaze hover:underline" data-testid="thanks-call-link">
                  03 9113 6005
                </a>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-16 border-t border-slate-200 pt-12">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-blaze">Our Moving Services</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2.5" data-testid="thanks-services">
                {SERVICES.map((s) => (
                  <Link
                    key={s}
                    to="/#services"
                    className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-blaze hover:text-blaze"
                  >
                    {s}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>

        </div>
      </section>
      <Footer />
    </main>
  );
}
